import { useState, type ComponentProps } from 'react';
import { TextField, Button } from '@cmsgov/ds-medicare-gov';
import { checkForPII } from '@shared/helpers/piiCheck';
import { trackSearchInitiated } from '@shared/analytics';
import type { Lang } from '../constants';
import { t } from '../i18n';
import './SearchBar.css';

interface SearchBarProps {
  lang: Lang;
  onSearch: (keyword: string) => void;
  initialKeyword?: string;
  disabled?: boolean;
}

export function SearchBar({ lang, onSearch, initialKeyword = '', disabled }: Readonly<SearchBarProps>) {
  const [value, setValue] = useState(initialKeyword);
  const [error, setError] = useState('');
  const strings = t(lang);

  const handleSubmit: NonNullable<ComponentProps<'form'>['onSubmit']> = (e) => {
    e.preventDefault();
    const trimmed = value.trim();

    const piiResult = checkForPII(trimmed, lang);
    if (piiResult.hasPII) {
      setError(piiResult.message);
      return;
    }

    setError('');
    onSearch(trimmed);
    trackSearchInitiated({
      searchType: 'app_library',
      resultCount: 0, // Will be updated after results load
      searchTerm: trimmed,
    });
  };

  return (
    <div className="app-library-search__search-bar">
      <form
        className="app-library-search__search-bar-form"
        onSubmit={handleSubmit}
        role="search"
      >
        <div className="app-library-search__search-bar-form-container">
          <TextField
            label={strings.searchLabel}
            labelClassName="ds-u-visibility--screen-reader"
            name="app-search-keyword"
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            errorMessage={error || undefined}
            disabled={disabled}
            className="app-library-search__search-bar-input"
          />
          <Button type="submit" variation="solid" disabled={disabled} className="app-library-search__search-bar-button">
            <i aria-hidden="true" className="fa-solid fa-magnifying-glass app-library-search__search-bar-button-icon" />
            <span className="app-library-search__search-bar-button-text">{strings.searchButton}</span>
          </Button>
        </div>
  </form>
    </div>
  );
}
